/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-report-template-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useQuery } from '@apollo/client/react';
import { OpraReportTemplate } from '@interfaces/report-templates.interface';
import React, { useEffect, useState } from 'react';
import { CUSTOM_REPORT_TEMPLATE_COLUMNS } from '@components/admin/admin-custom-report-template-grid/admin-custom-report-template-grid.data';
import {
    filterRetiredAndPublishedTemplates,
    sortByNameAndSetDates,
} from '@components/admin/admin-custom-report-template-grid/admin-custom-report-template-grid.settings';
import Loading from '@components/core/loading';
import Error from '@components/error/error';
import AddCustomReportTemplateDialog from '../add-custom-report-template-dialog';
import { useAuthorization, useBoolean, useTranslation } from 'hooks/index';
import { AuditServiceType } from 'types/graphql-global-types';
import { stringDateWithoutTimeFromUtc } from 'utils/convertor';
import EditOPRAReportTemplateDialog from 'views/components/admin/edit-opra-report-template-dialog';
import ExpandableGridV2 from 'views/components/core/expandable/grid-v2';
import {
    ExcelExportConfig,
    IActionItemV2,
} from 'views/components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from 'views/components/core/grid-header/grid-menu/grid-menu.interface';
import Constants from '@utils/constants';
import { addPrefixedRecordIdsToTemplates, addPrefixedOrganisationTemplateId } from '@utils/prefixes-helper';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { getOrganisation } from '@queries/generated/uam.generated';
import { GET_ORGANISATION } from '@queries/uam';
import { useGetPowerBIReportTemplates, useGetReportTemplatesForOrg } from '@apis/bff-api/report-templates';

import './styles.scss';

const AdminCustomReportTemplateGrid = ({ orgKey }: { orgKey: string }) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const canEdit = !!user?.canWriteSystemAdminLevel?.();
    const openAddReportTemplateDialog = useBoolean(false);
    const openEditOPRAReportTemplateDialog = useBoolean(false);
    const openEditCustomReportTemplateDialog = useBoolean(false);
    const [currentReportTemplate, setCurrentReportTemplate] = useState<OpraReportTemplate | null>(null);
    const [reportTemplatesList, setReportTemplatesList] = useState<OpraReportTemplate[]>([]);

    const {
        data: orgReportTemplatesData,
        isLoading: isLoadingOrgReportTemplates,
        error: orgReportTemplatesError,
        mutate: refetchOrgReportTemplates,
    } = useGetReportTemplatesForOrg(orgKey);
    const {
        data: pbiReportTemplatesData,
        isLoading: isLoadingPbiReportTemplates,
        error: pbiReportTemplatesError,
        mutate: refetchPbiReportTemplates,
    } = useGetPowerBIReportTemplates(orgKey);
    const {
        data: systemPbiReportTemplatesData,
        isLoading: isLoadingSystemPbiReportTemplates,
        error: systemPbiReportTemplatesError,
        mutate: refetchSystemPbiReportTemplates,
    } = useGetPowerBIReportTemplates(Constants.ObjectId.Empty);
    const { data: orgsData, loading: isLoadingOrg } = useQuery<getOrganisation>(GET_ORGANISATION);
    const currentOrgName = orgsData?.organisation?.name;
    const { data: prefixesData, loading: isLoadingPrefixes } = useQuery<auditRecordIdPrefixes>(
        AUDIT_RECORD_ID_PREFIXES,
        { fetchPolicy: 'cache-first' },
    );

    const isLoadingData =
        isLoadingOrgReportTemplates || isLoadingPbiReportTemplates || isLoadingOrg || isLoadingPrefixes;

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.right,
            tooltipTitle: t('admin:addTemplate'),
            onClick: () => openAddReportTemplateDialog.setTrue(),
            customClassName: 'grid-header__item--always-visible',
            icon: ['fal', 'plus'],
            disabled: !canEdit,
            testId: 'add-template-menu-button',
        },
    ];

    const actions = (template: OpraReportTemplate): IActionItemV2[] => [
        {
            label: 'admin:editTemplate',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentReportTemplate(template);
                if (template.templateTypeName === 'OPRA') {
                    openEditOPRAReportTemplateDialog.setTrue();
                } else {
                    openEditCustomReportTemplateDialog.setTrue();
                }
            },
            enabled: canEdit,
        },
    ];

    const exportFileName = `${currentOrgName}_Report Templates_${stringDateWithoutTimeFromUtc(
        new Date(),
        'DD-MMM-YYYY',
    )}`;

    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
    };

    const onClose = async (shouldUpdate?: boolean | null) => {
        if (openAddReportTemplateDialog.value) openAddReportTemplateDialog.setFalse();
        if (openEditCustomReportTemplateDialog.value) openEditCustomReportTemplateDialog.setFalse();
        if (openEditOPRAReportTemplateDialog.value) openEditOPRAReportTemplateDialog.setFalse();
        if (shouldUpdate) {
            const [orgTemplates, pbiTemplates, systemPbiReportTemplates] = await Promise.all([
                refetchOrgReportTemplates(),
                refetchPbiReportTemplates(),
                refetchSystemPbiReportTemplates(),
            ]);
            const filteredReportTemplates = filterRetiredAndPublishedTemplates(orgTemplates);
            const sortedReportTemplates = sortByNameAndSetDates(t, filteredReportTemplates, pbiTemplates);
            const templatesWithPrefixedIds = addPrefixedRecordIdsToTemplates(
                sortedReportTemplates,
                AuditServiceType.REPORTING,
                prefixesData?.auditRecordIdPrefixes,
            );
            const fullyFormedTemplates = addPrefixedOrganisationTemplateId(
                templatesWithPrefixedIds,
                AuditServiceType.REPORTING,
                prefixesData?.auditRecordIdPrefixes,
            );
            setReportTemplatesList(fullyFormedTemplates);
        }
        if (currentReportTemplate) setCurrentReportTemplate(null);
    };

    useEffect(() => {
        if (isLoadingOrgReportTemplates || isLoadingPbiReportTemplates) return;
        const filteredReportTemplates = filterRetiredAndPublishedTemplates(orgReportTemplatesData);
        const sortedReportTemplates = sortByNameAndSetDates(t, filteredReportTemplates, pbiReportTemplatesData);
        const templatesWithPrefixedIds = addPrefixedRecordIdsToTemplates(
            sortedReportTemplates,
            AuditServiceType.REPORTING,
            prefixesData?.auditRecordIdPrefixes,
        );
        const fullyFormedTemplates = addPrefixedOrganisationTemplateId(
            templatesWithPrefixedIds,
            AuditServiceType.REPORTING,
            prefixesData?.auditRecordIdPrefixes,
        );
        setReportTemplatesList(fullyFormedTemplates);
    }, [isLoadingOrgReportTemplates, isLoadingPbiReportTemplates]);

    if (isLoadingData) return <Loading />;
    if (orgReportTemplatesError) return <Error error={orgReportTemplatesError} />;
    if (pbiReportTemplatesError) return <Error error={pbiReportTemplatesError} />;
    if (systemPbiReportTemplatesError) return <Error error={systemPbiReportTemplatesError} />;

    return (
        <>
            {(openAddReportTemplateDialog.value ||
                (openEditCustomReportTemplateDialog.value && !!currentReportTemplate)) && (
                <AddCustomReportTemplateDialog
                    orgKey={orgKey}
                    currentReportTemplate={currentReportTemplate}
                    pbiReportTemplatesData={pbiReportTemplatesData ?? []}
                    onClose={onClose}
                />
            )}
            {openEditOPRAReportTemplateDialog.value && (
                <EditOPRAReportTemplateDialog
                    orgKey={orgKey}
                    currentReportTemplate={currentReportTemplate}
                    onClose={onClose}
                />
            )}
            <ExpandableGridV2
                title={t('admin:templates')}
                hideCheckbox
                hideExpand
                className="custom-report-templates-grid"
                customKey="report-template"
                key="report-template"
                id="report-template"
                items={reportTemplatesList}
                rowActions={actions}
                scrollable="scrollable"
                groupable
                reorderable
                resizable
                fixedScroll
                pageable={false}
                columns={CUSTOM_REPORT_TEMPLATE_COLUMNS(t)}
                extraMenuItems={extraMenuItems}
                exportConfig={exportConfig}
            />
        </>
    );
};

export default AdminCustomReportTemplateGrid;
